import type { DisplayDateFormat, MembershipSummary, TenantMemberProfile } from '@livechat/shared';

import { TenantApi } from './TenantApi.js';
import { TenantAi } from './TenantAi.js';
import { isAiSettingsSection, type AiSettingsSection } from '../ai-settings-navigation.js';
import { TenantBlockedVisitors } from './TenantBlockedVisitors.js';
import { TenantDepartments } from './TenantDepartments.js';
import { TenantIntegration, type IntegrationSettingsSection } from './TenantIntegration.js';
import { TenantOperations } from './TenantOperations.js';
import { TenantSettings } from './TenantSettings.js';
import { TenantUpdates } from './TenantUpdates.js';
import { TenantWebhooks } from './TenantWebhooks.js';

export type SettingsSection =
	| 'user'
	| IntegrationSettingsSection
	| 'departments'
	| 'blocked-visitors'
	| 'webhooks'
	| 'operations'
	| 'updates'
	| 'api'
	| AiSettingsSection;

interface SettingsNavigationItem {
	label: string;
	section: SettingsSection;
}

const SETTINGS_NAVIGATION: SettingsNavigationItem[] = [
	{ label: 'User Settings', section: 'user' },
	{ label: 'Integration', section: 'integration' },
	{ label: 'Chatbox', section: 'widget' },
	{ label: 'Offline Form', section: 'offline-form' },
	{ label: 'Departments', section: 'departments' },
	{ label: 'Blocked Visitors', section: 'blocked-visitors' },
	{ label: 'Webhooks', section: 'webhooks' },
	{ label: 'Operations', section: 'operations' },
	{ label: 'Updates', section: 'updates' },
	{ label: 'API', section: 'api' },
	{ label: 'AI', section: 'ai' },
];

export function settingsNavigation(membership: MembershipSummary): SettingsNavigationItem[] {
	const administrativeSections: SettingsSection[] = [
		'integration',
		'widget',
		'offline-form',
		'webhooks',
		'operations',
		'api',
	];
	return SETTINGS_NAVIGATION.filter(({ section }) => {
		if (section === 'ai') return membership.role === 'owner' || membership.role === 'admin';
		if (section === 'updates') return membership.role !== 'agent';
		return (
			!administrativeSections.includes(section) ||
			membership.role === 'owner' ||
			membership.role === 'admin'
		);
	});
}

export function TenantSettingsWorkspace({
	currentUserId,
	departmentId,
	membership,
	onDateFormatChanged,
	onDepartmentSelected,
	knowledgeCollectionSlug,
	onKnowledgeCollectionSelected,
	onProfileChanged,
	onMemberDeleted,
	section,
	targetUserId,
}: {
	currentUserId: string;
	departmentId?: string;
	membership: MembershipSummary;
	onDateFormatChanged: (format: DisplayDateFormat) => void;
	onDepartmentSelected: (departmentId?: string, replace?: boolean) => void;
	knowledgeCollectionSlug?: string;
	onKnowledgeCollectionSelected?: (slug?: string, replace?: boolean) => void;
	onProfileChanged: (profile: TenantMemberProfile) => void;
	onMemberDeleted: () => void;
	section: SettingsSection;
	targetUserId?: string;
}) {
	const available = settingsNavigation(membership);
	const activeSection = available.some(
		(item) =>
			item.section === section || (item.section === 'ai' && isAiSettingsSection(section)),
	)
		? section
		: 'user';

	return (
		<div className="settings-content">
			{activeSection === 'user' && (
				<TenantSettings
					currentUserId={currentUserId}
					membership={membership}
					onProfileChanged={onProfileChanged}
					onMemberDeleted={onMemberDeleted}
					targetUserId={targetUserId}
				/>
			)}
			{(['integration', 'widget', 'offline-form'] as const).includes(
				activeSection as IntegrationSettingsSection,
			) && (
				<TenantIntegration
					membership={membership}
					onDateFormatChanged={onDateFormatChanged}
					section={activeSection as IntegrationSettingsSection}
				/>
			)}
			{activeSection === 'departments' && (
				<TenantDepartments
					departmentId={departmentId}
					membership={membership}
					onDepartmentSelected={onDepartmentSelected}
				/>
			)}
			{activeSection === 'blocked-visitors' && (
				<TenantBlockedVisitors membership={membership} />
			)}
			{activeSection === 'webhooks' && <TenantWebhooks membership={membership} />}
			{activeSection === 'operations' && <TenantOperations membership={membership} />}
			{activeSection === 'updates' && <TenantUpdates membership={membership} />}
			{activeSection === 'api' && <TenantApi membership={membership} />}
			{isAiSettingsSection(activeSection) && (
				<TenantAi
					membership={membership}
					section={activeSection}
					knowledgeCollectionSlug={knowledgeCollectionSlug}
					onKnowledgeCollectionSelected={onKnowledgeCollectionSelected}
				/>
			)}
		</div>
	);
}
